import axios from 'axios';

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

export class PushService {
    /**
     * Checks if Push Notifications are supported and permissions are granted/denied.
     */
    static isSupported(): boolean {
        return 'serviceWorker' in navigator && 'PushManager' in window;
    }

    /**
     * Requests notification permission from the user.
     */
    static async requestPermission(): Promise<NotificationPermission> {
        if (!this.isSupported()) {
            throw new Error('Push notifications are not supported by this browser.');
        }
        return await Notification.requestPermission();
    }

    /**
     * Subscribes the user to push notifications.
     * 1. Registers Service Worker (if not already)
     * 2. Subscribes using VAPID key
     * 3. Sends subscription object to backend
     */
    static async subscribeToPush(): Promise<PushSubscription | null> {
        if (!this.isSupported()) {
            console.warn('Push not supported');
            return null;
        }

        if (!VAPID_PUBLIC_KEY) {
            console.error('Missing VAPID Public Key configuration');
            return null;
        }

        try {
            const registration = await navigator.serviceWorker.ready;

            const convertedVapidKey = this.urlBase64ToUint8Array(VAPID_PUBLIC_KEY);

            // subscribe() returns the EXISTING subscription unchanged if one already exists
            // with the same applicationServerKey, or creates a new one if none exists.
            // We do NOT force-unsubscribe here — doing so kills the FCM token immediately,
            // which causes any in-flight push to return 410 and triggers the auto-remove cycle.
            // Token staleness is handled by the backend 410 flow + pushsubscriptionchange handler.
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: convertedVapidKey
            });

            await this.sendSubscriptionToBackend(subscription);

            return subscription;
        } catch (error) {
            console.error('Failed to subscribe the user: ', error);
            throw error;
        }
    }

    /**
     * Unsubscribes the user from push notifications.
     */
    static async unsubscribeFromPush(): Promise<void> {
        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();

            if (subscription) {
                // Determine deviceId derived from endpoint (same as backend)
                const endpoint = subscription.endpoint;

                await subscription.unsubscribe();

                // Also notify backend to delete device entry
                await this.removeDevice(endpoint);
            }
        } catch (error) {
            console.error('Error unsubscribing', error);
        }
    }

    /**
     * Removes a device from the backend by its endpoint or explicit deviceId.
     */
    static async removeDevice(identifier: string): Promise<void> {
        try {
            // If identifier is an endpoint URL, send it as a query parameter
            if (identifier.startsWith('http')) {
                await axios.delete(`/subscription/device`, {
                    params: { endpoint: identifier },
                    withCredentials: true
                });
            } else {
                // Otherwise assume it's a deviceId hex hash
                await axios.delete(`/subscription/device/${identifier}`, {
                    withCredentials: true
                });
            }
        } catch (error) {
            console.error('Failed to remove device from backend:', error);
            throw error;
        }
    }

    /**
     * Silently re-sends an existing subscription to the backend.
     * Safe to call on every app open — the backend upserts by deviceId so it's idempotent.
     * Useful after a pushsubscriptionchange that couldn't reach the backend (expired auth, etc.).
     */
    static async syncSubscriptionWithBackend(subscription: PushSubscription): Promise<void> {
        return this.sendSubscriptionToBackend(subscription);
    }

    /**
     * Sends the subscription object to the backend API.
     */
    private static async sendSubscriptionToBackend(subscription: PushSubscription): Promise<void> {
        try {
            await axios.post(`/subscription/device`, subscription, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json'
                }
            });

        } catch (error) {
            console.error('Failed to send subscription to backend:', error);
            // Suppress error so as not to break the UI flow, usually.
        }
    }

    /**
     * Utility to convert VAPID key.
     */
    private static urlBase64ToUint8Array(base64String: string): Uint8Array {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }

    /**
     * Sends a local test notification via the service worker to confirm push is working.
     * Does not go through the backend — purely client-side.
     */
    static async sendTestNotification(): Promise<void> {
        const registration = await navigator.serviceWorker.ready;
        await registration.showNotification('✅ Schneller-Wohnen — Alles eingerichtet!', {
            body: 'Du bekommst ab jetzt Benachrichtigungen für neue Wohnungen in deinem Suchgebiet.',
            icon: '/logo192.png',
            badge: '/logo192.png',
            tag: 'test-notification',
        });
    }
}
